import { Container, Spinner, XStack, YStack, gutterSize } from '@my/ui'
import React from 'react'
import {
  MainLayout,
  NoAccess,
  Meta,
  NAV_HEIGHT_SM,
  SidebarLayout,
  SECONDARY_NAV_HEIGHT,
} from 'app/components'
import { CreatePostButton, GroupPostCard, GroupNoResults, GroupEventCard, GroupAnnouncementCard } from '../../components'
import { GroupCardHorizontal, GroupCardCover, GroupCardVertical } from '../../containers'
import { useAuth } from 'app/provider/auth'
import { useInView } from 'react-intersection-observer'
import { AccessBanner } from './components'
import { canJoinGroup, canLeaveGroup } from '../../helpers'
import { GroupQueryOutput } from '../../queries'
import { GroupPostOrAd } from './group-screen.container'
import { GroupAdBlockRenderer } from 'app/features/ads/blocks'

interface GroupPostScreenProps {
  HeaderComponent?: React.ReactNode
  isMember: boolean
  isAdmin: boolean
  canInteract: boolean
  pendingRequest: boolean
  group?: GroupQueryOutput['Group']
  noResults?: boolean
  groupPosts: GroupPostOrAd[]
  totalResults: number
  groupPostsLoading?: boolean
  groupPostsFetching?: boolean
  groupPostsFetchingMore?: boolean
  hasMore?: boolean
  fetchMore?: () => void
  refetchUserGroups: () => void
  refetchGroupPosts: () => void
  onCreatePostOpenChange: (open: boolean) => void
}

export function GroupScreen(props: GroupPostScreenProps) {
  const { group, groupPosts } = props
  const { user } = useAuth()

  const [fetchMoreElRef] = useInView({
    onChange: (inView) => {
      if (!props.hasMore || !inView) {
        return
      }
      props.fetchMore && props.fetchMore()
    },
    threshold: 0,
    delay: 50,
    rootMargin: '0px 0px 40% 0px',
  })

  const isPrivate = group?.visibility === 'private'
  const groupHref = `/groups/${group?.id}`

  return (
    <MainLayout>
      <Meta
        title={group?.meta?.title || group?.name || 'Group'}
        description={group?.meta?.description || group?.description}
        image={group?.profileImage?.sizes?.medium?.url}
      />
      {props.HeaderComponent && props.HeaderComponent}
      <SidebarLayout
        pt={0}
        $gtMd={{ pt: '$3.5' }}
        containerProps={{
          gutter: 'gtXs',
        }}
        sidebarContainerProps={{
          top: NAV_HEIGHT_SM + SECONDARY_NAV_HEIGHT + 16,
        }}
        SidebarContent={
          group && (
            <GroupCardVertical
              id={group.id}
              name={group.name}
              totalMembers={group.totalMembers}
              coverImage={group.coverImage?.sizes.cover}
              profileImage={group.profileImage?.sizes.medium}
              description={group.description}
              visibility={group.visibility}
              isAdmin={props.isAdmin}
              hasPendingRequest={props.pendingRequest}
              href={groupHref}
              canJoin={!!user && canJoinGroup({ group, user })}
              hasJoined={props.isMember}
              canLeave={props.isMember && !props.isAdmin && canLeaveGroup({ group })}
              shareable
              flex={1}
              imagePriority
            />
          )
        }
      >
        {group && (
          <YStack $gtMd={{ display: 'none' }}>
            <GroupCardCover coverImage={group.coverImage?.sizes.cover} br="$0" imagePriority />
            <GroupCardHorizontal
              id={group.id}
              name={group.name}
              totalMembers={group.totalMembers}
              profileImage={group.profileImage?.sizes.medium}
              visibility={group.visibility}
              href={`/groups/${group.id}`}
              canJoin={!!user && canJoinGroup({ group, user })}
              hasJoined={props.isMember}
              canLeave={props.isMember && !props.isAdmin && canLeaveGroup({ group })}
              isAdmin={props.isAdmin}
              hasPendingRequest={props.pendingRequest}
            />
          </YStack>
        )}

        {user && (
          <AccessBanner
            allowedUserTypes={group?.allowedUserTypes || []}
            userType={user.userType || ''}
            isMember={!!props.isMember}
            isPrivate={isPrivate}
          />
        )}

        {/* Not signed in */}
        {!user && (
          <NoAccess
            title={
              isPrivate
                ? 'This is a Private Group. You must Sign In or Sign Up to Student Saviour to join this group.'
                : 'This is a Public Group. Join or Sign In to Student Saviour to join this group.'
            }
          />
        )}

        {props.isMember && props.canInteract && (
          <CreatePostButton
            user={user}
            onPress={() => props.onCreatePostOpenChange(true)}
            $xs={{
              mx: gutterSize,
            }}
          />
        )}

        {props.noResults && (
          <Container gutter>
            <GroupNoResults
              title="Sorry, there’s no posts available at the moment"
              description="But don’t worry, refresh and try again or join a group to get your feed and running!"
            />
          </Container>
        )}

        {groupPosts.length > 0 && (
          <YStack gap="$3.5">
            {groupPosts.map((item, i) => {
              if (item.type === 'ad') {
                return <GroupAdBlockRenderer key={`Ad:${i}`} {...item.data} />
              }

              if (item.type === 'groupPost') {
                const post = item.data
                return (
                  <GroupPostCard
                    key={post.id}
                    user={user}
                    onAfterDelete={props.refetchGroupPosts}
                    canInteract={props.canInteract}
                    canExpandContent={true}
                    post={post}
                  />
                )
              }

              if (item.type === 'groupAnnouncement') {
                const announcement = item.data
                return (
                  <GroupAnnouncementCard
                    key={announcement.id}
                    user={user}
                    onAfterDelete={props.refetchGroupPosts}
                    canInteract={props.canInteract}
                    canExpandContent={true}
                    announcement={announcement}
                  />
                )
              }

              if (item.type === 'groupEvent') {
                const event = item.data
                return (
                  <GroupEventCard
                    key={event.id}
                    user={user}
                    canInteract={props.canInteract}
                    event={event}
                    onAfterDelete={props.refetchGroupPosts}
                    $xs={{
                      mx: gutterSize,
                    }}
                  />
                )
              }

              return null
            })}
          </YStack>
        )}

        {(props.groupPostsLoading || props.groupPostsFetching || props.groupPostsFetchingMore) && (
          <XStack w="100%" jc="center" ai="center" h={100}>
            <Spinner />
          </XStack>
        )}

        <XStack w="100%" opacity={0} ref={fetchMoreElRef as any} />
      </SidebarLayout>
    </MainLayout>
  )
}
